import { useEffect } from "react";
import { Button, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { desktop, isNative } from "@/lib/platform";
import { useDesktopNotificationWatcher } from "@/hooks/useDesktopNotificationWatcher";

/**
 * US-013 AC21 / AC17 — native shell deep-link + auto-update handling. Renders
 * nothing on the web.
 *
 * - Deep link: the shell delivers an in-app route target; we navigate there
 *   (full navigation — same app origin, allowed by the nav lockdown; works with
 *   BrowserRouter without a router hook). Electron sources it from
 *   `livingsyncs://app/<route>`; Capacitor from `appUrlOpen` (US-015).
 * - Update downloaded: surface a notice with a "Restart now" action. The shell
 *   ALSO installs it unattended once the machine hits the hard sign-out idle
 *   threshold (see desktop session.ts), because these machines are typically
 *   never quit — a shared Mac in a med room runs for weeks, and
 *   `autoInstallOnAppQuit` alone leaves this notice standing indefinitely. The
 *   button is for the user who wants the update sooner than the overnight
 *   window. No-op under Capacitor — iPadOS updates come from the App Store.
 * - Update failed: replace that notice, because a downloaded update can still
 *   fail to install and the optimistic message would otherwise stand forever.
 *
 * US-015 AC18: gated on `isNative`, not `isElectron`, so it also mounts inside
 * the Capacitor iPad shell. The component name is kept for now to avoid an
 * unrelated rename in this change.
 */

/**
 * Shared id so the failure notice REPLACES the "Update ready" one instead of
 * appearing beside a message that has become false.
 */
const UPDATE_NOTIFICATION_ID = "desktop-update-status";
export function DesktopAppEvents() {
  useEffect(() => {
    if (!desktop) return;
    const ds = desktop;

    const offDeepLink = ds.onDeepLink((route) => {
      // Only in-app paths: must start with a single "/" (reject "//host"
      // protocol-relative targets and anything non-path).
      if (route.startsWith("/") && !route.startsWith("//")) {
        window.location.assign(route);
      }
    });

    const offUpdate = ds.onUpdateDownloaded((version) => {
      notifications.show({
        id: UPDATE_NOTIFICATION_ID,
        title: "Update ready",
        // Says what will happen on its own AND offers the shortcut. The old
        // copy ("will install the next time you restart") was the only thing
        // on offer, and on a machine nobody quits it was simply untrue.
        message: (
          <Stack gap="xs" align="flex-start">
            <Text size="sm">
              Version {version} installs automatically next time this machine is
              left idle. You can apply it now instead.
            </Text>
            <Button
              size="xs"
              variant="light"
              onClick={() => {
                ds.quitAndInstall();
              }}
            >
              Restart now
            </Button>
          </Stack>
        ),
        color: "blue",
        autoClose: false,
      });
    });

    // A download succeeding is NOT an install succeeding. Squirrel.Mac
    // validates the update's code signature against the running app's
    // requirement and refuses the swap on a mismatch — which happened for
    // real on the ad-hoc -> Developer ID transition (2026-08-14). The
    // "Update ready" notice above had already been shown, nothing ever
    // retracted it, and the user restarted repeatedly with no change and no
    // explanation while the actual reason sat in a local log file.
    //
    // So: REPLACE the optimistic notice (same notification id) rather than
    // stacking a second one beside a message that is now false. The
    // "signature" copy deliberately does not say "try again" — restarting
    // can never resolve that case, only reinstalling can.
    const offUpdateError = ds.onUpdateError((kind) => {
      notifications.hide(UPDATE_NOTIFICATION_ID);
      notifications.show({
        id: UPDATE_NOTIFICATION_ID,
        title: "Update could not be installed",
        message:
          kind === "signature"
            ? "This update can't be applied over the installed version. Reinstall from the latest installer to keep receiving updates."
            : kind === "download"
              ? "The update didn't download correctly. It will be retried automatically."
              : "Something went wrong applying the update. Support can check the app logs.",
        color: kind === "download" ? "yellow" : "red",
        autoClose: false,
      });
    });

    return () => {
      offDeepLink();
      offUpdate();
      offUpdateError();
    };
  }, []);

  // US-018: `isNative` is a module-level constant, fixed for the whole
  // app's lifetime (captured once from `window.livingsyncs` / `window.Capacitor`
  // at load) — it never changes across renders, so conditionally MOUNTING a
  // separate component (rather than conditionally SKIPPING hook calls inside one
  // component) is safe here and keeps the notification watcher's data
  // hooks (useMyTasks/useMedicationsDue/useIncidents) from ever being
  // called at all on the web build.
  return isNative ? <DesktopNotificationWatcherMount /> : null;
}

function DesktopNotificationWatcherMount() {
  useDesktopNotificationWatcher();
  return null;
}
